import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookOpen, HeartPulse, PhoneCall, Stethoscope, Activity, Users, User } from "lucide-react";
import { Navigation } from "../components/navigation";
import { Footer } from "../components/footer";
import "./noviceDetails/noviceDetails.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ?? "";
const buildApiUrl = (endpoint) => (API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint);

const getImageSource = (novice) =>
  novice?.imageUrl?.trim() || novice?.image?.trim() || novice?.imageKey?.trim() || "/img/team/01.jpg";

const extractNovice = (payload) => {
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object") return payload.data;
  if (payload.item && typeof payload.item === "object") return payload.item;
  return payload;
};

const normalizeList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.data && Array.isArray(payload.data)) return payload.data;
  if (payload.items && Array.isArray(payload.items)) return payload.items;
  return [];
};

const formatAgeReadable = (dobValue, fallback = "—") => {
  if (!dobValue) return fallback;
  const dobDate = new Date(dobValue);
  if (Number.isNaN(dobDate.getTime())) return fallback;
  const now = new Date();
  let years = now.getFullYear() - dobDate.getFullYear();
  let months = now.getMonth() - dobDate.getMonth();
  if (now.getDate() < dobDate.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`);
  if (!parts.length) return "0 months";
  return parts.join(" ");
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatValue = (value, fallback = "—") => (value != null && value !== "" ? value : fallback);

export const NoviceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [novice, setNovice] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [buildingMap, setBuildingMap] = useState({});
  const [roomMap, setRoomMap] = useState({});
  const [classMap, setClassMap] = useState({});
  const [regionMap, setRegionMap] = useState({});
  const [townshipMap, setTownshipMap] = useState({});
  const [parentRecord, setParentRecord] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [behavioralRecords, setBehavioralRecords] = useState([]);
  const [activeSectionTitle, setActiveSectionTitle] = useState("");

  useEffect(() => {
    if (!id) {
      setStatus("error");
      setError(new Error("No novice selected"));
      setNovice(null);
      return;
    }

    let isMounted = true;

    const fetchNovice = async () => {
      setStatus("loading");
      setError(null);
      try {
        const response = await fetch(buildApiUrl(`/novices/${id}`));
        if (!response.ok) throw new Error("Unable to load novice");
        const payload = await response.json();
        const record = extractNovice(payload);
        if (isMounted) {
          setNovice(record);
          setStatus("loaded");
        }
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setError(err);
          setStatus("error");
        }
      }
    };

    fetchNovice();
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;
    const fetchLookup = async (endpoint, setter, idField, labelField) => {
      try {
        const response = await fetch(buildApiUrl(endpoint));
        if (!response.ok) return;
        const payload = normalizeList(await response.json());
        const map = {};
        payload.forEach((entry) => {
          const key = entry?.[idField] ?? entry?.id;
          const label = entry?.[labelField] ?? entry?.name;
          if (key != null && label) {
            map[String(key)] = label;
          }
        });
        if (isMounted) {
          setter(map);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchLookup("/buildings", setBuildingMap, "building_id", "building_name");
    fetchLookup("/rooms", setRoomMap, "room_id", "room_name");
    fetchLookup("/classes", setClassMap, "class_id", "class_name");
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchLookup = async (endpoint, setter, idField, labelField) => {
      try {
        const response = await fetch(buildApiUrl(endpoint));
        if (!response.ok) return;
        const payload = normalizeList(await response.json());
        const map = {};
        payload.forEach((entry) => {
          const key = entry?.[idField] ?? entry?.id;
          const label = entry?.[labelField] ?? entry?.name;
          if (key != null && label) {
            map[String(key)] = label;
          }
        });
        if (isMounted) {
          setter(map);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchLookup("/regions", setRegionMap, "region_id", "region_name");
    fetchLookup("/townships", setTownshipMap, "township_id", "township_name");

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchMetricList = useCallback(
    (endpoint, setter) => {
      if (!id) {
        setter([]);
        return undefined;
      }
      let isMounted = true;
      const controller = new AbortController();
      const fetchMetric = async () => {
        try {
          const response = await fetch(
            buildApiUrl(`${endpoint}?novice_id=${encodeURIComponent(id)}`),
            { signal: controller.signal }
          );
          if (!response.ok) throw new Error(`Unable to load ${endpoint}`);
          const payload = normalizeList(await response.json());
          if (isMounted) {
            setter(payload);
          }
        } catch (err) {
          if (controller.signal.aborted) return;
          console.error(`Failed to load ${endpoint}`, err);
          if (isMounted) {
            setter([]);
          }
        }
      };
      fetchMetric();
      return () => {
        isMounted = false;
        controller.abort();
      };
    },
    [id]
  );

  useEffect(() => fetchMetricList("/health-metrics", setHealthRecords), [fetchMetricList]);
  useEffect(() => fetchMetricList("/medical-histories", setMedicalRecords), [fetchMetricList]);
  useEffect(() => fetchMetricList("/behavioral-metrics", setBehavioralRecords), [fetchMetricList]);

  useEffect(() => {
    let isMounted = true;
    const parentId = novice?.parent_id ?? novice?.parent?.parent_id;
    if (!parentId) {
      setParentRecord(null);
      return;
    }

    const fetchParent = async () => {
      try {
        const response = await fetch(buildApiUrl(`/parents/${parentId}`));
        if (!response.ok) throw new Error("Unable to load parent");
        const payload = await response.json();
        if (isMounted) {
          setParentRecord(payload);
        }
      } catch (err) {
        console.error("Failed to load parent", err);
        if (isMounted) {
          setParentRecord(null);
        }
      }
    };

    fetchParent();
    return () => {
      isMounted = false;
    };
  }, [novice]);

  const detailData = useMemo(() => {
    if (!novice) return { heroStats: [], sections: [] };
    const noviceName = novice.novice_name || "Novice";
    const name = novice.name || "No Name Yet";
    const building =
      buildingMap[String(novice.building_id)] ||
      novice.building?.building_name ||
      novice.building_name;
    const room =
      roomMap[String(novice.room_id)] ||
      novice.room?.room_name ||
      novice.room_name;
    const className =  classMap[String(novice.class_id)] || novice.class?.class_name || novice.class_name;
    const region =
      regionMap[String(novice.region_id)] ||
      novice.region?.region_name ||
      novice.region_name;
    const township =
      townshipMap[String(novice.township_id)] ||
      novice.township?.township_name ||
      novice.township_name;
    const parentDetail = parentRecord ?? novice.parent ?? {};
    const motherRegion =
      regionMap[String(parentDetail.mother_region_id)] ||
      parentDetail.motherRegion?.region_name ||
      parentDetail.mother_region_name;
    const motherTownship =
      townshipMap[String(parentDetail.mother_township_id)] ||
      parentDetail.motherTownship?.township_name ||
      parentDetail.mother_township_name;
    const fatherRegion =
      regionMap[String(parentDetail.father_region_id)] ||
      parentDetail.fatherRegion?.region_name ||
      parentDetail.father_region_name;
    const fatherTownship =
      townshipMap[String(parentDetail.father_township_id)] ||
      parentDetail.fatherTownship?.township_name ||
      parentDetail.father_township_name;
    const latestHealth = healthRecords.reduce((current, record) => {
      if (!record) return current;
      if (!current) return record;
      const currentTime = new Date(current.created_at).getTime() || 0;
      const recordTime = new Date(record.created_at).getTime() || 0;
      return recordTime > currentTime ? record : current;
    }, null);
    const latestMedical = medicalRecords.reduce((current, record) => {
      if (!record) return current;
      if (!current) return record;
      const currentTime = new Date(current.created_at).getTime() || 0;
      const recordTime = new Date(record.created_at).getTime() || 0;
      return recordTime > currentTime ? record : current;
    }, null);
    const latestBehavioral = behavioralRecords.reduce((current, record) => {
      if (!record) return current;
      if (!current) return record;
      const currentTime = new Date(current.created_at).getTime() || 0;
      const recordTime = new Date(record.created_at).getTime() || 0;
      return recordTime > currentTime ? record : current;
    }, null);
    const formatMeasurement = (value, unit) =>
      value != null ? `${Number(value).toFixed(1)}${unit ? ` ${unit}` : ""}` : null;
    const joined = novice.join_date || novice.joined_date || novice.start_date || novice.date_joined;
    const monkDate = novice.monk_date;
    const dob = novice.dob;
    const ageDisplay = formatAgeReadable(dob, formatValue(novice.age));

    const heroStats = [
      { label: "Building", value: formatValue(building, "Unassigned") },
      { label: "Room", value: formatValue(room, "Unassigned") },
      { label: "Age", value: ageDisplay },
      { label: "Join Date", value: formatDate(joined) },
      { label: "Monk Date", value: formatDate(monkDate) },
    ];

    const sections = [
      {
        title: "ကိုယ်ရေး အကျဥ်း",
        icon: <User aria-hidden="true" size={16} />,
        rows: [
          { label: "Novice Name", value: noviceName },
          { label: "Name", value: name },
          { label: "Form No", value: formatValue(novice.form_no) },
          { label: "Type", value: formatValue(novice.type?.type_name || novice.type_name || novice.type_id) },
          { label: "Age", value: formatAgeReadable(dob, formatValue(novice.age)) },
          { label: "Monk Date", value: formatDate(monkDate) },
          { label: "Place of Birth", value: formatValue(novice.place_of_birth) },
          { label: "Region", value: formatValue(region) },
          { label: "Township", value: formatValue(township) },
          { label: "Building", value: formatValue(building, "Unassigned") },
          { label: "Room", value: formatValue(room, "Unassigned") },
          { label: "Class", value: formatValue(className) },
        ],
      },
      {
        title: "နောက်ဆုံးတက်ရောက်ခဲ့သည့်ကျောင်း",
        icon: <BookOpen aria-hidden="true" size={16} />,
        rows: [   
          { label: "School", value: formatValue(novice.school_name) },
        ],
      },
      {
        title: "တာဝန်ခံ",
        icon: <PhoneCall aria-hidden="true" size={16} />,
        rows: [
          { label: "Contact Person", value: formatValue(novice.contact_person) },
          { label: "Contact Number", value: formatValue(novice.contact_number) },
        ],
      },
      {
        title: "မိဘများ",
        icon: <Users aria-hidden="true" size={16} />,
        rows: [
          { label: "Mother Name", value: formatValue(parentDetail.mother_name) },
          { label: "Mother's Phone", value: formatValue(parentDetail.mother_phone_no) },
          { label: "Mother's NRC", value: formatValue(parentDetail.mother_nrc_number) },
          { label: "Mother's Address", value: formatValue(parentDetail.mother_address) },
          { label: "Mother's Region", value: formatValue(motherRegion) },
          { label: "Mother's Township", value: formatValue(motherTownship) },
          { label: "Father's Name", value: formatValue(parentDetail.father_name) },
          { label: "Father's Phone", value: formatValue(parentDetail.father_phone_no) },
          { label: "Father's NRC", value: formatValue(parentDetail.father_nrc_number) },
          { label: "Father's Address", value: formatValue(parentDetail.father_address) },
          { label: "Father's Region", value: formatValue(fatherRegion) },
          { label: "Father's Township", value: formatValue(fatherTownship) },
        ],
      },
      {
        title: "Health Records",
        icon: <HeartPulse aria-hidden="true" size={16} />,
        rows: [
          { label: "Height", value: formatMeasurement(latestHealth?.height, "cm") },
          { label: "Weight", value: formatMeasurement(latestHealth?.weight, "kg") },
          { label: "Blood Pressure", value: formatValue(latestHealth?.blood_pressure) },
          { label: "Remark", value: formatValue(latestHealth?.remark) },
          { label: "Recorded At", value: formatDate(latestHealth?.created_at) },
        ],
      },
      {
        title: "Medical History",
        icon: <Stethoscope aria-hidden="true" size={16} />,
        rows: [
          { label: "Date", value: formatDate(latestMedical?.date) },
          { label: "Remark", value: formatValue(latestMedical?.remark) },
        ],
      },
      {
        title: "Behavioral Metrics",
        icon: <Activity aria-hidden="true" size={16} />,
        rows: [
          { label: "Date", value: formatDate(latestBehavioral?.date) },
          { label: "Type", value: formatValue(latestBehavioral?.type) },
          { label: "Remark", value: formatValue(latestBehavioral?.remark) },
        ],
      },
    ];

    const personalSectionTitle = "ကိုယ်ရေး အကျဥ်း";
    const visibleSections = sections.filter((section) => section.title === personalSectionTitle);

    return { heroStats, sections: visibleSections };
  }, [
    novice,
    buildingMap,
    roomMap,
    classMap,
    regionMap,
    townshipMap,
    parentRecord,
    healthRecords,
    medicalRecords,
    behavioralRecords,
  ]);

  useEffect(() => {
    if (!detailData.sections.length) {
      setActiveSectionTitle("");
      return;
    }
    setActiveSectionTitle((current) =>
      detailData.sections.some((section) => section.title === current)
        ? current
        : detailData.sections[0].title
    );
  }, [detailData.sections]);

  const activeSection =
    detailData.sections.find((section) => section.title === activeSectionTitle) ??
    detailData.sections[0];
  const headerName = novice?.novice_name || novice?.name || "Novice";
  const isLoading = status === "loading";
  const hasError = status === "error";

  return (
    <>
      <Navigation />
      <main className="novice-details">
        <div className="novice-details__page-heading">
          <h1>ကိုယ်ရေး အကျဥ်း</h1>
        </div>
        <section className="novice-details__layout">
          <article className="novice-details__hero-card">
            <div className="novice-details__hero-top">
              <div className="novice-details__avatar">
                <img src={getImageSource(novice)} alt={headerName} />
              </div>
              <div className="novice-details__header-copy">
                <p className="novice-details__subtitle">Novice profile</p>
                <h2>{isLoading ? "Loading…" : headerName}</h2>
              </div>
              <button
                className="novice-details__close"
                type="button"
                aria-label="Close detail view"
                onClick={() => navigate(-1)}
              >
                ×
              </button>
            </div>
            <div className="novice-details__stat-row">
              {detailData.heroStats.map((stat) => (
                <div className="novice-details__stat" key={stat.label}>
                  <p>{stat.label}</p>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>
          </article>

          {hasError ? (
            <div className="novice-details__error-panel">
              <p className="novice-details__error">{error?.message || "Unable to load novice data"}</p>
            </div>
          ) : (
            <>
              <div className="novice-details__content-wrapper">
                <div className="novice-details__tabs-column">
                  {detailData.sections.map((section, index) => (
                    <div className="novice-details__tab-group" key={section.title}>
                      <button
                        type="button"
                        className={`novice-details__tab ${
                          section.title === activeSection?.title ? "novice-details__tab--active" : ""
                        }`}
                        onClick={() => setActiveSectionTitle(section.title)}
                      >
                        <span className="novice-details__tab-icon">{section.icon}</span>
                        <span className="novice-details__tab-label">{section.title}</span>
                      </button>
                      {index < detailData.sections.length - 1 && (
                        <hr className="novice-details__tab-divider" aria-hidden="true" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="novice-details__panel-column">
                  {activeSection ? (
                    <article className="novice-details__tab-panel">
                      <h3>{activeSection.title}</h3>
                      <dl>
                        {activeSection.rows.map((row) => (
                          <div className="novice-details__section-row" key={row.label}>
                            <dt>{row.label}</dt>
                            <dd>{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </article>
                  ) : (
                    <div className="novice-details__empty">
                      <p>No details available yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default NoviceDetailsPage;
